/**
 * ContractorsSection Component
 *
 * Reusable section component for displaying contractors list with loading state.
 * Wraps ContractorsList with container styling and loading/empty states.
 *
 * FEATURES:
 * - Built-in loading state with spinner/skeleton
 * - Empty state handling
 * - Container styling (container-auto, padding)
 * - Full integration with ContractorsList
 * - Customizable via props
 *
 * USAGE:
 * <ContractorsSection
 *   contractors={contractors}
 *   isLoading={isLoading}
 *   categoryName="Cleaning"
 *   subcategoryName="Home Cleaning"
 * />
 *
 * @example
 * // Basic usage
 * <ContractorsSection
 *   contractors={contractorsList}
 *   isLoading={false}
 * />
 *
 * @example
 * // With category context
 * <ContractorsSection
 *   contractors={contractors}
 *   isLoading={loading}
 *   categoryName="Repair Services"
 *   subcategoryName="Appliance Repair"
 * />
 *
 * @example
 * // Custom styling and display count
 * <ContractorsSection
 *   contractors={contractors}
 *   isLoading={false}
 *   initialDisplayCount={12}
 *   containerClassName="bg-surface-primary py-16"
 *   listClassName="max-w-7xl mx-auto"
 * />
 *
 * @example
 * // Custom loading message
 * <ContractorsSection
 *   contractors={[]}
 *   isLoading={true}
 *   loadingMessage="Finding the best contractors for you..."
 * />
 *
 * @example
 * // Custom empty state
 * <ContractorsSection
 *   contractors={[]}
 *   isLoading={false}
 *   emptyTitle="No contractors available"
 *   emptyMessage="Please try different filters or check back later"
 * />
 */

'use client';

import { type HTMLAttributes } from 'react';
import { cn } from '@/src/shared/lib/utils';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { ContractorsList } from '@/src/widgets/contractors/ContractorsList';
import type { ContractorCardProps } from '@/src/entities/contractor/ui/ContractorCard';

export interface ContractorsSectionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Array of contractors to display
   */
  contractors: ContractorCardProps[];

  /**
   * Loading state - shows loading message when true
   * @default false
   */
  isLoading?: boolean;

  /**
   * Category name for context (passed to ContractorsList)
   */
  categoryName?: string;

  /**
   * Subcategory name for context (passed to ContractorsList)
   */
  subcategoryName?: string;

  /**
   * Initial number of contractors to display before "Load More"
   * @default 8
   */
  initialDisplayCount?: number;

  /**
   * Custom loading message
   * @default "Loading contractors..."
   */
  loadingMessage?: string;

  /**
   * Custom empty state title
   * @default "No contractors found"
   */
  emptyTitle?: string;

  /**
   * Custom empty state message
   * @default "Try adjusting your filters or search criteria"
   */
  emptyMessage?: string;

  /**
   * Show section even if empty (for debugging or layout purposes)
   * @default false
   */
  showWhenEmpty?: boolean;

  /**
   * Custom container classes
   */
  containerClassName?: string;

  /**
   * Custom classes for ContractorsList
   */
  listClassName?: string;

  /**
   * Handler for location icon click (navigate to map)
   */
  onLocationClick?: (id: string) => void;
}

/**
 * ContractorsSection Component
 *
 * Section wrapper for ContractorsList with loading and empty states.
 * Provides consistent layout across pages that display contractors.
 */
export const ContractorsSection: React.FC<ContractorsSectionProps> = ({
  contractors,
  isLoading = false,
  categoryName,
  subcategoryName,
  initialDisplayCount = 8,
  loadingMessage = 'Loading contractors...',
  emptyTitle = 'No contractors found',
  emptyMessage = 'Try adjusting your filters or search criteria',
  showWhenEmpty = false,
  containerClassName,
  listClassName,
  onLocationClick,
  className,
  ...props
}) => {
  // Don't render if empty and showWhenEmpty is false
  if (!isLoading && contractors.length === 0 && !showWhenEmpty) {
    return null;
  }

  return (
    <section
      className={cn('container-auto py-8', containerClassName, className)}
      {...props}
    >
      {/* Loading State */}
      {isLoading ? (
        <div className="text-center py-12">
          <Typography variant="h3" color="secondary">
            {loadingMessage}
          </Typography>
        </div>
      ) : contractors.length > 0 ? (
        /* Contractors List */
        <ContractorsList
          contractors={contractors}
          categoryName={categoryName}
          subcategoryName={subcategoryName}
          initialDisplayCount={initialDisplayCount}
          onLocationClick={onLocationClick}
          className={listClassName}
        />
      ) : (
        /* Empty State */
        <div className="text-center py-12">
          <Typography variant="h3" color="secondary">
            {emptyTitle}
          </Typography>
          <Typography variant="body" color="secondary" className="mt-2">
            {emptyMessage}
          </Typography>
        </div>
      )}
    </section>
  );
};

ContractorsSection.displayName = 'ContractorsSection';

// Re-export types for convenience
export type { ContractorCardProps };
